import { useCallback, useRef, useState } from "react";
import { Facebook, Users, X, ExternalLink } from "lucide-react";

// ── Config ─────────────────────────────────────────────────────
export const FB_GROUP_URL = "https://www.facebook.com/share/g/1A5JQBe4NY/";

const SEEN_PROFILES_KEY = "haven_profiles_seen";
const PROMPT_STATE_KEY = "haven_fb_prompt_state";

// Fire the dialog after the user has viewed this many UNIQUE profiles
const PROMPT_THRESHOLD = 10;

// If they dismiss, wait this long before showing again
const RESHOW_COOLDOWN_MS = 30 * 24 * 60 * 60 * 1000; // 30 days

// Cap on how many seen profile IDs to persist (keeps localStorage small)
const MAX_TRACKED_PROFILES = 200;

// ── localStorage helpers ───────────────────────────────────────
function loadSeenProfiles() {
  try {
    const raw = localStorage.getItem(SEEN_PROFILES_KEY);
    if (!raw) return new Set();
    const arr = JSON.parse(raw);
    return Array.isArray(arr) ? new Set(arr) : new Set();
  } catch {
    return new Set();
  }
}

function persistSeenProfiles(set) {
  try {
    // Keep only the most recent N IDs
    const arr = Array.from(set).slice(-MAX_TRACKED_PROFILES);
    localStorage.setItem(SEEN_PROFILES_KEY, JSON.stringify(arr));
  } catch {
    /* localStorage disabled or full — non-fatal */
  }
}

function loadPromptState() {
  try {
    const raw = localStorage.getItem(PROMPT_STATE_KEY);
    if (!raw) return { joined: false, dismissedAt: 0 };
    const parsed = JSON.parse(raw);
    return {
      joined: Boolean(parsed?.joined),
      dismissedAt: Number(parsed?.dismissedAt) || 0,
    };
  } catch {
    return { joined: false, dismissedAt: 0 };
  }
}

function persistPromptState(state) {
  try {
    localStorage.setItem(PROMPT_STATE_KEY, JSON.stringify(state));
  } catch {
    /* non-fatal */
  }
}

// ── Hook ───────────────────────────────────────────────────────
/**
 * Returns state + callbacks for managing the Facebook group promo dialog.
 *
 * Usage in a page:
 *   const fb = useFacebookGroupPrompt();
 *   useEffect(() => {
 *     profiles.forEach(p => fb.markProfileSeen(p.user_id));
 *   }, [profiles, fb.markProfileSeen]);
 *   <FacebookGroupDialog open={fb.visible} onClose={fb.dismiss} onJoin={fb.markJoined} seenCount={fb.seenCount} />
 */
export function useFacebookGroupPrompt() {
  const seenRef = useRef(loadSeenProfiles());
  const promptStateRef = useRef(loadPromptState());
  const shownThisSessionRef = useRef(false);

  const [seenCount, setSeenCount] = useState(seenRef.current.size);
  const [visible, setVisible] = useState(false);

  const maybeShow = useCallback(() => {
    if (shownThisSessionRef.current) return;                    // already shown this session
    if (promptStateRef.current.joined) return;                  // already joined
    if (seenRef.current.size < PROMPT_THRESHOLD) return;        // not enough browsing yet

    // Cooldown after dismissal
    const sinceDismissed = Date.now() - (promptStateRef.current.dismissedAt || 0);
    if (sinceDismissed < RESHOW_COOLDOWN_MS) return;

    shownThisSessionRef.current = true;
    setVisible(true);
  }, []);

  const markProfileSeen = useCallback((profileId) => {
    if (!profileId) return;
    if (seenRef.current.has(profileId)) return;   // already counted
    seenRef.current.add(profileId);
    persistSeenProfiles(seenRef.current);
    setSeenCount(seenRef.current.size);
    maybeShow();
  }, [maybeShow]);

  const dismiss = useCallback(() => {
    promptStateRef.current = {
      ...promptStateRef.current,
      dismissedAt: Date.now(),
    };
    persistPromptState(promptStateRef.current);
    setVisible(false);
  }, []);

  const markJoined = useCallback(() => {
    promptStateRef.current = {
      joined: true,
      dismissedAt: promptStateRef.current.dismissedAt,
    };
    persistPromptState(promptStateRef.current);
    setVisible(false);
  }, []);

  return {
    visible,
    seenCount,
    markProfileSeen,
    dismiss,
    markJoined,
  };
}

// ── Dialog component ───────────────────────────────────────────
export default function FacebookGroupDialog({ open, onClose, onJoin, seenCount = 0 }) {
  if (!open) return null;

  const handleJoin = () => {
    // Mark as joined BEFORE opening the tab, so a slow tab open can't
    // re-trigger the dialog on the next markProfileSeen call.
    onJoin?.();
    window.open(FB_GROUP_URL, "_blank", "noopener,noreferrer");
  };

  return (
    <div
      className="fixed inset-0 z-[80] flex items-end sm:items-center justify-center p-0 sm:p-4 bg-black/50"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-t-2xl sm:rounded-2xl w-full sm:max-w-md shadow-2xl overflow-hidden animate-fade-in"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="relative bg-gradient-to-br from-[#7C3AED] to-[#EC4899] px-6 pt-8 pb-6 text-center text-white">
          <button
            onClick={onClose}
            className="absolute top-3 right-3 p-1.5 rounded-full bg-white/15 hover:bg-white/25 transition-colors"
            aria-label="Close"
          >
            <X className="w-4 h-4" />
          </button>

          <div className="w-14 h-14 rounded-2xl bg-white/20 backdrop-blur-sm flex items-center justify-center mx-auto mb-3">
            <Users className="w-7 h-7" />
          </div>
          <h2 className="font-display text-xl font-semibold">
            Help us grow Haven 💜
          </h2>
          <p className="text-white/85 text-sm mt-1.5">
            {seenCount > 0
              ? ``
              : ""}
          </p>
        </div>

        {/* Body */}
        <div className="p-6 space-y-4">
          <p className="text-sm text-[#404040] leading-relaxed">
            Join our Facebook community to help more people find Haven. The
            more of us there are, the more matches you'll have.
          </p>

          <ul className="space-y-2 text-sm text-[#404040]">
            <li className="flex items-start gap-2">
              <span className="text-[#7C3AED] font-bold mt-0.5">•</span>
              <span>Meet other Haven members outside the app</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-[#7C3AED] font-bold mt-0.5">•</span>
              <span>Help us reach people who don't know about us yet</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-[#7C3AED] font-bold mt-0.5">•</span>
              <span>Share experiences and support each other</span>
            </li>
          </ul>

          <button
            onClick={handleJoin}
            className="w-full inline-flex items-center justify-center gap-2 bg-[#1877F2] text-white py-3 rounded-xl font-semibold text-sm hover:bg-[#0f66d9] transition-colors"
          >
            <Facebook className="w-4 h-4" />
            Join the Facebook group
            <ExternalLink className="w-3.5 h-3.5 opacity-80" />
          </button>

          <button
            onClick={onClose}
            className="w-full text-sm text-[#6B6B70] hover:text-[#0F0F10] py-2 transition-colors"
          >
            Maybe later
          </button>
        </div>
      </div>
    </div>
  );
}